import { Div, Dropdown, Flex, Gutter, OptionListOption, Title } from '@gaiads/telia-react-component-library';
import { TeliaTextInput } from '@teliads/components/react';
import { useTranslation } from 'react-i18next';

import { Channel } from '@cdaas/api-clients/dist/order-admin-openapi';

import { CompanyInformationErrors } from '../utils';
import { EMAIL_OR_EMPTY_STRING_REGEX } from '../../../utils';

import styles from './CompanyInformation.module.scss';

interface CompanyInformationProps {
  addMode: boolean;
  tscid: string | undefined;
  setTscId: (tscId: string) => void;
  partyId: string | null;
  companyName: string;
  setCompanyName: (companyName: string) => void;
  businessId: string | undefined;
  setBusinessId: (businessId: string) => void;
  vatId: string | undefined;
  setVatId: (vatId: string) => void;
  teliaFinanceNumber: string | undefined;
  setTeliaFinanceNumber: (teliaFinanceNumber: string) => void;
  technicalContactEmail: string | null;
  setTechnicalContactEmail: (technicalContactEmail: string | null) => void;
  regionOptions: OptionListOption[];
  regionSelected?: OptionListOption;
  setRegionSelected: (selected: OptionListOption) => void;
  orderChannel: Channel | undefined;
  finnishSorviId: string | null;
  setFinnishSorviId: (sorviId: string) => void;
  errors: CompanyInformationErrors | undefined;
}

export default function CompanyInformation(props: CompanyInformationProps) {
  const {
    addMode,
    tscid,
    setTscId,
    partyId,
    companyName,
    setCompanyName,
    businessId,
    setBusinessId,
    vatId,
    setVatId,
    teliaFinanceNumber,
    setTeliaFinanceNumber,
    technicalContactEmail,
    setTechnicalContactEmail,
    regionOptions,
    regionSelected,
    setRegionSelected,
    orderChannel,
    finnishSorviId,
    setFinnishSorviId,
    errors,
  } = props;

  const { t } = useTranslation('organizationForm');

  return (
    <Div padding={{ size: 'lg' }} boxShadow="md">
      <Title size={'sm'}>{t('company_information.company_information')}</Title>
      <Flex directionColumn className={styles['customer-organization-form__company-info__company-info-inputs-container']}>
        <Gutter size="layout-5" />
        <TeliaTextInput
          label={t('company_information.tscid_input_label')}
          onValueChange={(e: CustomEvent) => setTscId(e.detail.target.value)}
          value={tscid || ''}
          required={true}
          requiredErrorMessage={t('company_information.tscid_input_required_error')}
          errorMessage={errors?.tscId}
        />
        <Gutter size="layout-5" />
        <TeliaTextInput
          label={t('company_information.company_name_input_label')}
          onValueChange={(e: CustomEvent) => setCompanyName(e.detail.target.value)}
          value={companyName}
          required={true}
          requiredErrorMessage={t('company_information.company_name_input_required_error')}
          errorMessage={errors?.companyName}
          disabled={regionSelected?.value === 'NO'}
        />
        <Gutter size="layout-5" />
        <TeliaTextInput
          label={t('company_information.customer_business_id_input_label')}
          onValueChange={(e: CustomEvent) => setBusinessId(e.detail.target.value)}
          value={businessId || ''}
          required={true}
          requiredErrorMessage={t('company_information.customer_business_id_input_required_error')}
          errorMessage={errors?.businessId}
          disabled={regionSelected?.value === 'NO'}
        />
        <Gutter size="layout-5" />
        <TeliaTextInput
          label={t('company_information.customer_vat_id_input_label')}
          onValueChange={(e: CustomEvent) => setVatId(e.detail.target.value)}
          value={vatId || ''}
          required={true}
          requiredErrorMessage={t('company_information.customer_vat_id_input_required_error')}
          errorMessage={errors?.vatId}
        />
        <Gutter size="layout-5" />
        <TeliaTextInput
          label={t('company_information.telia_finance_call_off_number_input_label')}
          onValueChange={(e: CustomEvent) => setTeliaFinanceNumber(e.detail.target.value)}
          value={teliaFinanceNumber || ''}
          required={true}
          requiredErrorMessage={t('company_information.telia_finance_call_off_number_input_required_error')}
          errorMessage={errors?.teliaFinanceNumber}
        />
        <Gutter size="layout-5" />
        <TeliaTextInput
          label={t('company_information.technical_contact_email_input_label')}
          onValueChange={(e: CustomEvent) => setTechnicalContactEmail(e.detail.target.value ? e.detail.target.value : null)}
          value={technicalContactEmail || ''}
          inputmode="email"
          pattern={EMAIL_OR_EMPTY_STRING_REGEX.source}
          patternErrorMessage={t('company_information.technical_contact_email_input_invalid_error')}
          errorMessage={errors?.technicalContactEmail}
        />
        <Gutter size="layout-5" />
        <Dropdown
          label={t('company_information.region_configuration_input_label')}
          name="region"
          onChange={(_, selected) => setRegionSelected(selected)}
          selectedOption={regionSelected}
          options={regionOptions}
          required={true}
          disabled={true}
        />
        {!addMode && (
          <>
            <Gutter size="layout-5" />
            <Dropdown
              label={t('company_information.order_channel_input_label')}
              name="orderChannel"
              selectedOption={{ label: orderChannel?.code || '', value: orderChannel?.code || '' }}
              options={[]}
              onChange={() => undefined}
              disabled={true}
            />
          </>
        )}
        <Gutter size="layout-5" />
        {regionSelected?.value === 'FI' && (
          <>
            <TeliaTextInput
              label={t('company_information.sorvi_id_input_label')}
              onValueChange={(e: CustomEvent) => setFinnishSorviId(e.detail.target.value)}
              value={finnishSorviId || ''}
              required={true}
              requiredErrorMessage={t('company_information.sorvi_id_required_error')}
              errorMessage={errors?.finnishSorviId}
            />
            <Gutter size="layout-5" />
          </>
        )}

        <TeliaTextInput label={t('company_information.party_id_input_label')} value={partyId || ''} disabled={true} />
      </Flex>
    </Div>
  );
}
